import {useSelector} from 'react-redux'
import {isFreeCancellationOffer} from 'business/offer'
import {
  getNightlyDisplayTotalPrice,
  shouldAnchorPriceBeVisible
} from 'business/price'
import {getMealsOnly} from 'business/roomOfferMeals'
import useParams from 'components/data/URLParams/useParams'
import {getOfferCheck} from 'modules/offerCheck/selectors'
import {
  getAnchorPriceNightlyTotal,
  getRaaAnchorPriceBreakdown
} from 'modules/raaOffers/selectors'
import {
  getCheapestOffer,
  getOfferById,
  getSelectedOfferPosition,
  getSelectedRoomPosition
} from 'modules/rooms/selectors'
import {getSapiAnchorPriceNightlyTotal} from 'modules/searchApi/selectors'
import {toggle} from 'opticks'

import {CheckoutOfferContext} from '@daedalus/core/src/analytics/types/Events'
import {getIsPrivateDeal} from '@daedalus/core/src/auth/modules/selectors'
import {BookingSources} from '@daedalus/core/src/booking/types/BookingData'
import {isExistingBooking} from '@daedalus/core/src/booking/utils'
import {getCancellationDeadline} from '@daedalus/core/src/offer/business/cancellationPenalties'
import {isEligibleForChargeLater} from '@daedalus/core/src/offer/business/chargeLater'
import {getIsPayAtPropertyMoR} from '@daedalus/core/src/offer/business/getIsPayAtPropertyMoR'
import {BOFH_PROXY_PROVIDER_CODE} from '@daedalus/core/src/offer/business/isBofhOffer'
import {
  GET_OFFER_CHECK_QUERY_KEY,
  offerCheckApi
} from '@daedalus/core/src/offer/services/offerCheckApi'
import {
  getDisplayPrice,
  getTotalPrice
} from '@daedalus/core/src/price/business/price'

interface Params {
  offerId?: string
  roomId?: string
  roomPosition?: number
  offerPosition?: number
  isSplitBooking?: boolean
}

const useOfferContext = ({
  offerId,
  roomId,
  roomPosition,
  offerPosition,
  isSplitBooking = false
}: Params): CheckoutOfferContext | null => {
  const {
    offer_token,
    offerId: urlOfferId,
    providerCode: urlProviderCode,
    roomId: urlRoomId
  } = useParams()

  const roomsOffer = useSelector(getOfferById(roomId, offerId))
  const offerCheckRoom = useSelector(getOfferCheck)?.room

  const {data: offerCheck} = useSelector(
    offerCheckApi.endpoints.getOfferCheck.select(GET_OFFER_CHECK_QUERY_KEY)
  )

  const selectedRoomPosition = useSelector(getSelectedRoomPosition)
  const selectedOfferPosition = useSelector(getSelectedOfferPosition)
  const raaAnchorPriceBreakdown = useSelector(getRaaAnchorPriceBreakdown)
  const cheapestOffer = useSelector(getCheapestOffer)
  const isPrivateDeal = useSelector(getIsPrivateDeal(roomsOffer))
  const sapiAnchorPriceNightlyTotal = useSelector(
    getSapiAnchorPriceNightlyTotal
  )

  const offer = roomsOffer || offerCheck

  if (!offer) {
    // Providing fallback context if offer is not available
    return {
      anchorPrice: 0,
      availableRooms: 0,
      bookURI: '',
      cancellationPolicy: {
        freeRefundableUntil: null,
        description: null
      },
      canPayLater: false,
      chargeType: [],
      cug: [],
      displayPricePerRoomPerNight: 0,
      hasFreeCancellation: false,
      isPrivateDeal: false,
      meals: [],
      offerId: urlOfferId || '',
      offerPosition: null,
      providerCode: urlProviderCode,
      providerFeedId: null, // Attach to the context when providerFeedId is available via SAPI rooms()
      proxyProviderCode: BOFH_PROXY_PROVIDER_CODE,
      providerRateType: '',
      roomId: urlRoomId || '',
      roomName: '',
      roomPosition: null,
      totalPrice: 0,
      offerToken: offer_token,
      ...(isSplitBooking && {isSplitBookingElegible: isSplitBooking})
    }
  }

  const {prices} = offer
  const services = roomsOffer?.services || []
  const providerRateType = roomsOffer?.providerRateType || ''

  const raaAnchorPriceNightlyTotal = raaAnchorPriceBreakdown
    ? getAnchorPriceNightlyTotal(raaAnchorPriceBreakdown)
    : null
  const anchorPrice = toggle(
    '30a4c716-new-get-offers-endpoint',
    raaAnchorPriceNightlyTotal,
    sapiAnchorPriceNightlyTotal
  )

  const cheapestOfferNightlyDisplayTotalPrice =
    cheapestOffer && getNightlyDisplayTotalPrice(cheapestOffer.prices)

  const displayPricePerRoomPerNight = getNightlyDisplayTotalPrice(prices)
  const isAnchorPriceShown = shouldAnchorPriceBeVisible(
    anchorPrice,
    displayPricePerRoomPerNight,
    cheapestOfferNightlyDisplayTotalPrice
  )

  const price = getDisplayPrice(prices)

  const cancellationDeadline = getCancellationDeadline(
    offer.cancellationPenalties
  )

  const providerCode = roomsOffer?.providerCode || urlProviderCode

  const finalRoomPosition =
    roomPosition === undefined ? selectedRoomPosition : roomPosition
  const finalOfferPosition =
    offerPosition === undefined ? selectedOfferPosition : offerPosition

  const isChargeLaterEligible = isEligibleForChargeLater(
    providerCode,
    offer.cancellationPenalties,
    true
  )

  const totalPrice = getTotalPrice(price)
  const chargeDate = !isExistingBooking(offerCheck) && offerCheck?.chargeDueAt

  const chargeType = isChargeLaterEligible
    ? BookingSources.ChargeLater
    : BookingSources.Default

  const paymentExpiryAt = {
    ...(isChargeLaterEligible && {
      chargeExpiryAt: chargeDate || undefined
    })
  }

  return {
    anchorPrice: isAnchorPriceShown && anchorPrice ? anchorPrice : undefined,
    availableRooms: roomsOffer?.availableRooms || 0,
    bookURI: offer.links[0]?.href,
    cancellationPolicy: {
      freeRefundableUntil: cancellationDeadline || null,
      description: null // not relevant for tracking
    },
    canPayLater:
      Boolean(roomsOffer?.canPayLater) ||
      getIsPayAtPropertyMoR(offerCheck) ||
      isChargeLaterEligible,
    chargeType: [chargeType],
    cug: roomsOffer?.cug || [],
    displayPricePerRoomPerNight,
    hasFreeCancellation: roomsOffer
      ? isFreeCancellationOffer(roomsOffer)
      : false,
    isPrivateDeal,
    meals: getMealsOnly(services),
    offerId: (offerId || urlOfferId) as string,
    offerPosition: finalOfferPosition || null,
    providerCode,
    providerFeedId: null, // Attach to the context when providerFeedId is available via SAPI rooms()
    proxyProviderCode: BOFH_PROXY_PROVIDER_CODE,
    providerRateType,
    roomId: offerCheckRoom?.id || '',
    roomName: offerCheckRoom?.name || '',
    roomPosition: finalRoomPosition || null,
    totalPrice,
    offerToken: offer_token,
    ...(isChargeLaterEligible && {paymentExpiryAt}),
    ...(isSplitBooking && {isSplitBookingElegible: isSplitBooking})
  }
}

export default useOfferContext
